/* =========================================================
   INTELLICLEAR PRIVATE PURCHASE PAGE
   /pages/private-purchase?items=VARIANT_ID:QTY,...[&from=HANDLE]

   - Reads only variant ids + quantities from the URL
   - Re-validates them through the app proxy
     (/apps/intelliclear-pricing/selection), which returns
     canonical Shopify titles, images, prices, subtotal and
     the secure checkout URL, only for a browser with valid
     private-pricing access (token from localStorage, sent in
     the POST body). Otherwise an access gate is shown.
   - Never trusts or displays anything from the URL itself
   - Quantity changes are re-validated and re-priced by the app
   ========================================================= */

(function () {
  'use strict';

  var MAX_LINES = 10;
  var MAX_QTY = 10;
  var REQUEST_TIMEOUT_MS = 15000;
  var UPDATE_DELAY_MS = 350;

  /* Written by intelliclear-pricing.js after a validated modal request. */
  var FALLBACK_KEY = 'stratusIntelliClearCheckoutFallback';
  var FALLBACK_MAX_AGE_MS = 24 * 60 * 60 * 1000;

  var HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]{0,254}$/;

  /* Written by intelliclear-pricing.js when /checkout grants access. */
  var ACCESS_KEY = 'stratusIntelliClearAccess';
  /* Read by intelliclear-pricing.js to preselect the same order after a gated request. */
  var PENDING_KEY = 'stratusIntelliClearPendingSelection';

  function readAccess() {
    try {
      var stored = JSON.parse(window.localStorage.getItem(ACCESS_KEY) || 'null');
      if (stored && typeof stored.token === 'string' && Date.parse(stored.expiresAt) > Date.now()) {
        return stored;
      }
      if (stored) window.localStorage.removeItem(ACCESS_KEY);
    } catch (error) {
      /* Storage unavailable */
    }
    return null;
  }

  function readAccessToken() {
    var access = readAccess();
    return access ? access.token : null;
  }

  /* "Saved for 30 days" right after a request; the real remaining time for a returning visit. */
  function accessNoteText() {
    var access = readAccess();
    var days = access ? Math.ceil((Date.parse(access.expiresAt) - Date.now()) / 86400000) : 0;
    if (days >= 29) return 'Private pricing access saved for 30 days on this device.';
    if (days > 1) return 'Private pricing access saved on this device for ' + days + ' more days.';
    return 'Private pricing access saved on this device until tomorrow.';
  }

  function clearAccessToken() {
    try {
      window.localStorage.removeItem(ACCESS_KEY);
    } catch (error) {
      /* Storage unavailable */
    }
  }

  function init(root) {
    if (!root || root.dataset.icPurchaseReady === 'true') return;
    root.dataset.icPurchaseReady = 'true';

    var endpoint = root.dataset.endpoint;
    var list = root.querySelector('[data-ic-purchase-items]');
    var order = root.querySelector('[data-ic-purchase-order]');
    var subtotalEl = root.querySelector('[data-ic-purchase-subtotal]');
    var summaryLines = root.querySelector('[data-ic-purchase-summary-lines]');
    var cta = root.querySelector('[data-ic-purchase-checkout]');
    var ctaLabel = root.querySelector('[data-ic-purchase-cta-label]');
    var updateError = root.querySelector('[data-ic-purchase-update-error]');
    var errorPanel = root.querySelector('[data-ic-purchase-error]');
    var retry = root.querySelector('[data-ic-purchase-retry]');
    var fallback = root.querySelector('[data-ic-purchase-fallback]');
    var edit = root.querySelector('[data-ic-purchase-edit]');
    var gate = root.querySelector('[data-ic-purchase-gate]');
    var requestLink = root.querySelector('[data-ic-purchase-request]');
    var masthead = root.querySelector('[data-ic-purchase-masthead]');
    var status = root.querySelector('[data-ic-purchase-status]');
    var accessNote = root.querySelector('[data-ic-purchase-access-note]');
    var hero = root.querySelector('[data-ic-purchase-hero]');
    var heroImage = root.querySelector('[data-ic-purchase-hero-image]');
    var support = root.querySelector('[data-ic-purchase-support]');
    var whyTitle = root.querySelector('[data-ic-purchase-why-title]');
    var whyCopy = root.querySelector('[data-ic-purchase-why-copy]');

    var params = new URLSearchParams(window.location.search);
    var requested = parseItems(params.get('items'));
    var lines = null; // last validated server lines
    var pending = null; // quantities waiting to be sent
    var updateTimer = null;
    var requestSeq = 0;
    var lastSubtotal = null; // last validated subtotal / checkout URL, kept if an update fails
    var lastCheckoutUrl = null;

    /* Edit selections: back to the originating product (modal reopens), else the replacements link. */
    var from = (params.get('from') || '').toLowerCase();
    if (edit && HANDLE_PATTERN.test(from)) {
      edit.href = root.dataset.productsBase + from + '#intelliclear-pricing';
    }
    /* The gate's request link reopens the request modal on that product. */
    if (requestLink && HANDLE_PATTERN.test(from)) {
      requestLink.href = root.dataset.productsBase + from + '#intelliclear-pricing';
    }


    /* ---------- URL ---------- */

    function parseItems(raw) {
      if (!raw) return [];
      var parts = raw.split(',');
      if (parts.length > MAX_LINES) return null;

      var seen = {};
      var items = [];
      for (var i = 0; i < parts.length; i++) {
        var match = /^([1-9][0-9]{0,19}):([1-9][0-9]?)$/.exec(parts[i].trim());
        if (!match || seen[match[1]] || Number(match[2]) > MAX_QTY) return null;
        seen[match[1]] = true;
        items.push({ variantId: match[1], quantity: Number(match[2]) });
      }
      return items;
    }

    function itemsString(items) {
      return items.map(function (i) { return i.variantId + ':' + i.quantity; }).join(',');
    }

    function numericId(gid) {
      return String(gid).replace(/^.*\//, '');
    }


    /* ---------- Formatting ---------- */

    function formatMoney(money) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: money.currencyCode
        }).format(Number(money.amount));
      } catch (error) {
        return money.amount + ' ' + money.currencyCode;
      }
    }

    function isSafeCheckoutUrl(value) {
      try {
        var url = new URL(value, window.location.origin);
        return url.protocol === 'https:' && url.pathname.indexOf('/cart/') === 0;
      } catch (error) {
        return false;
      }
    }

    function el(tag, className, text) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }


    /* ---------- Rendering ---------- */

    function setCheckout(url) {
      if (url && isSafeCheckoutUrl(url)) {
        lastCheckoutUrl = url;
        cta.href = url;
        cta.removeAttribute('aria-disabled');
        cta.classList.remove('is-busy');
        ctaLabel.textContent = 'Continue to Secure Checkout';
      } else {
        cta.removeAttribute('href');
        cta.setAttribute('aria-disabled', 'true');
      }
    }

    function setBusy() {
      cta.removeAttribute('href');
      cta.setAttribute('aria-disabled', 'true');
      cta.classList.add('is-busy');
      ctaLabel.textContent = 'Updating your order…';
    }

    function renderLines(data) {
      lastSubtotal = data.subtotal;
      list.innerHTML = '';

      data.items.forEach(function (line) {
        var item = el('li', 'ic-purchase__item');
        item.dataset.variantId = numericId(line.variantId);

        var media = el('span', 'ic-purchase__media');
        if (line.image && line.image.url) {
          var img = el('img', 'ic-purchase__image');
          img.src = line.image.url;
          img.alt = line.image.altText || line.productTitle;
          img.width = 160;
          img.height = 160;
          img.decoding = 'async';
          media.appendChild(img);
        } else {
          media.appendChild(el('span', 'ic-purchase__placeholder', '✓'));
        }

        /* The variant ("PCB Filter") is what was chosen; the product is its context. */
        var names = displayNames(line);
        var copy = el('div', 'ic-purchase__copy');
        if (names.context) copy.appendChild(el('span', 'ic-purchase__context', names.context));
        var nameEl = el('strong', 'ic-purchase__name', names.name);
        var pill = optionPill(line);
        if (pill) {
          var nameRow = el('span', 'ic-purchase__name-row');
          nameRow.appendChild(nameEl);
          nameRow.appendChild(el('span', 'ic-purchase__pill' + (pill.neutral ? ' ic-purchase__pill--neutral' : ''), pill.label));
          copy.appendChild(nameRow);
        } else {
          copy.appendChild(nameEl);
        }
        var description = lineDescription(line, pill);
        if (description) copy.appendChild(el('span', 'ic-purchase__desc', description));
        copy.appendChild(el('span', 'ic-purchase__unit', formatMoney(line.price) + ' each'));

        var qty = el('div', 'ic-purchase__qty');
        var minus = el('button', 'ic-purchase__qty-button', '−');
        minus.type = 'button';
        minus.dataset.step = '-1';
        minus.setAttribute('aria-label', 'Decrease quantity of ' + names.label);
        minus.disabled = line.quantity <= 1;
        var value = el('span', 'ic-purchase__qty-value', String(line.quantity));
        value.setAttribute('aria-label', 'Quantity ' + line.quantity);
        var plus = el('button', 'ic-purchase__qty-button', '+');
        plus.type = 'button';
        plus.dataset.step = '1';
        plus.setAttribute('aria-label', 'Increase quantity of ' + names.label);
        plus.disabled = line.quantity >= MAX_QTY;
        qty.appendChild(minus);
        qty.appendChild(value);
        qty.appendChild(plus);

        var total = el('span', 'ic-purchase__line-total', formatMoney(line.lineTotal));

        item.appendChild(media);
        item.appendChild(copy);
        item.appendChild(qty);
        item.appendChild(total);
        list.appendChild(item);
      });

      list.setAttribute('aria-busy', 'false');
      subtotalEl.textContent = formatMoney(data.subtotal);
      renderSummaryLines(data.items);
      renderContext(data.items);
    }

    /* Same option labels as the request modal, from the Shopify variant title the app returns. */
    function optionPill(line) {
      var title = (line.variantTitle || '').toLowerCase();
      if (title.indexOf('combo') !== -1) return { role: 'combo', label: 'Best value', neutral: false };
      if (title.indexOf('alkaline') !== -1) return { role: 'alkaline', label: 'Taste + minerals', neutral: true };
      if (title.indexOf('membrane') !== -1) return { role: 'membrane', label: 'Core filtration', neutral: true };
      if (title.indexOf('pcb') !== -1 || title.indexOf('dual filter') !== -1 || title.indexOf('filter pack') !== -1) {
        return { role: 'filter', label: 'Routine replacement', neutral: true };
      }
      return null;
    }

    /* "IntelliClear Pro600 – Replacement Filters" → "Pro600", as Shopify names the product. */
    function modelOf(line) {
      var match = /^IntelliClear®?\s+(.+?)\s+[–-]\s+Replacement\b/i.exec(line.productTitle || '');
      return match ? match[1] : '';
    }

    function forModel(line) {
      var model = modelOf(line);
      return model ? ' for your IntelliClear® ' + model : '';
    }

    /* One short line per item, only for recognised IntelliClear replacement types. */
    function lineDescription(line, pill) {
      if (!pill) return '';
      if (pill.role === 'combo') return 'Filter and RO membrane' + forModel(line) + ', in one order.';
      if (pill.role === 'filter') return 'Routine replacement filter' + forModel(line) + '.';
      if (pill.role === 'membrane') {
        var gpd = /\((\d+)\s*GPD\)/i.exec(line.variantTitle || '');
        return (gpd ? gpd[1] + ' GPD RO membrane' : 'RO membrane') + forModel(line) + '.';
      }
      if (pill.role === 'alkaline') return 'For IntelliClear® systems using the optional alkaline stage.';
      return '';
    }

    function joinNames(names) {
      if (names.length < 2) return names.join('');
      return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
    }

    /* Order-level guidance (block A) and the masthead image, from the same validated lines. */
    function renderContext(items) {
      if (support && whyTitle && whyCopy) {
        var title = '';
        var text = '';
        if (items.length === 1) {
          var line = items[0];
          var pill = optionPill(line);
          var role = pill ? pill.role : '';
          if (role === 'combo') {
            title = 'Why this package?';
            text = 'It puts the filter and RO membrane' + forModel(line) + ' in a single order, so you can replace both stages at once.';
          } else if (role === 'filter') {
            title = 'Why this filter?';
            text = 'Routine filter changes help keep flow and taste consistent. Replace it on schedule, or sooner if flow slows or taste changes.';
          } else if (role === 'membrane') {
            title = 'Why this membrane?';
            text = 'The RO membrane is the core filtration stage' + (modelOf(line) ? ' of your IntelliClear® ' + modelOf(line) : '') + '. Replace it if flow or water quality declines, or as recommended for your system.';
          } else if (role === 'alkaline') {
            title = 'Why this filter?';
            text = 'It adds minerals back to RO-filtered water for taste, on systems that use the optional alkaline stage.';
          }
        }
        if (!text) {
          var models = [];
          items.forEach(function (item) {
            var model = modelOf(item);
            if (model && models.indexOf(model) === -1) models.push(model);
          });
          title = 'About your order';
          text = (models.length ? 'Your order includes replacement parts for the IntelliClear® ' + joinNames(models) + '. ' : '') +
            'Each item shows the model it belongs to, so you can confirm everything matches your system before checkout.';
        }
        whyTitle.textContent = title;
        whyCopy.textContent = text;
        support.hidden = false;
      }

      /* One item only, and only with an image; never a single image for a multi-item order. */
      if (hero && heroImage) {
        var image = items.length === 1 && items[0].image && items[0].image.url ? items[0].image : null;
        if (!image) {
          clearHero();
        } else {
          heroImage.onerror = clearHero;
          heroImage.onload = function () { hero.hidden = false; };
          heroImage.src = largerImage(image.url);
        }
      }
    }

    /* The app returns 320px images; the CDN serves the same image wider for the masthead. */
    function largerImage(url) {
      try {
        var parsed = new URL(url, window.location.origin);
        if (parsed.searchParams.get('width') === '320') parsed.searchParams.set('width', '640');
        return parsed.toString();
      } catch (error) {
        return url;
      }
    }

    function clearHero() {
      if (!hero || !heroImage) return;
      hero.hidden = true;
      heroImage.onload = null;
      heroImage.onerror = null;
      heroImage.removeAttribute('src');
    }

    function clearContext() {
      clearHero();
      if (support) support.hidden = true;
      if (whyCopy) whyCopy.textContent = '';
    }

    function displayNames(line) {
      var hasVariant = line.variantTitle && line.variantTitle !== 'Default Title';
      return {
        context: hasVariant ? line.productTitle : '',
        name: hasVariant ? line.variantTitle : line.productTitle,
        label: hasVariant ? line.productTitle + ' – ' + line.variantTitle : line.productTitle
      };
    }

    /* Compact "name ×qty  amount" lines in the order summary (same server data). */
    function renderSummaryLines(items) {
      if (!summaryLines) return;
      summaryLines.innerHTML = '';

      items.forEach(function (line) {
        var row = el('li', 'ic-purchase__summary-line');
        row.dataset.variantId = numericId(line.variantId);
        var name = el('span', 'ic-purchase__summary-name', displayNames(line).name);
        name.appendChild(el('span', 'ic-purchase__summary-qty', ' × ' + line.quantity));
        row.appendChild(name);
        row.appendChild(el('span', 'ic-purchase__summary-amount', formatMoney(line.lineTotal)));
        summaryLines.appendChild(row);
      });
    }

    function showOrder() {
      errorPanel.hidden = true;
      gate.hidden = true;
      masthead.hidden = false;
      order.hidden = false;
    }

    /* Priced order on screen: only now is private pricing shown as active. */
    function markActive() {
      if (status) {
        status.hidden = false;
        status.classList.remove('is-pending');
      }
      if (accessNote) {
        accessNote.textContent = accessNoteText();
        accessNote.classList.remove('is-pending');
      }
    }

    /* No (or no longer valid) access: show the gate, never prices. */
    function showGate() {
      clearContext();
      order.hidden = true;
      errorPanel.hidden = true;
      masthead.hidden = true;
      if (status) status.hidden = true;
      list.innerHTML = '';
      if (summaryLines) summaryLines.innerHTML = '';
      subtotalEl.textContent = '—';
      setCheckout(null);
      gate.hidden = false;

      /* Remember the selection (ids + quantities only) for the request modal. */
      try {
        if (requested && requested.length) {
          window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ items: itemsString(requested), savedAt: Date.now() }));
        }
      } catch (error) {
        /* Enhancement only */
      }
    }

    function showError(allowFallback) {
      clearContext();
      order.hidden = true;
      gate.hidden = true;
      masthead.hidden = true;
      if (status) status.hidden = true;
      errorPanel.hidden = false;

      var url = allowFallback ? fallbackCheckoutUrl() : null;
      if (url) {
        fallback.href = url;
        fallback.hidden = false;
      } else {
        fallback.hidden = true;
        fallback.removeAttribute('href');
      }
    }

    /* A validated checkout URL from the modal, for exactly this selection, if the app is unreachable. */
    function fallbackCheckoutUrl() {
      try {
        var stored = JSON.parse(window.sessionStorage.getItem(FALLBACK_KEY) || 'null');
        if (
          stored &&
          requested &&
          stored.items === itemsString(requested) &&
          Date.now() - Number(stored.savedAt) < FALLBACK_MAX_AGE_MS &&
          isSafeCheckoutUrl(stored.checkoutUrl)
        ) {
          return stored.checkoutUrl;
        }
      } catch (error) {
        /* Enhancement only */
      }
      return null;
    }


    /* ---------- Requests ---------- */

    function fetchSelection(items, token) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = controller ? window.setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS) : null;

      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items, token: token }),
        signal: controller ? controller.signal : undefined
      })
        .then(function (response) {
          return response.json()
            .catch(function () { return {}; })
            .then(function (data) { return { status: response.status, data: data }; });
        })
        .catch(function () {
          return { status: 0, data: {} };
        })
        .then(function (result) {
          if (timer) window.clearTimeout(timer);
          return result;
        });
    }

    function isValid(data) {
      return Boolean(
        data &&
        data.ok === true &&
        Array.isArray(data.items) &&
        data.items.length > 0 &&
        data.subtotal &&
        isSafeCheckoutUrl(data.checkoutUrl)
      );
    }

    function isAccessRequired(result) {
      return result.status === 403 && result.data && result.data.error === 'private_access_required';
    }

    function load() {
      if (!requested || !requested.length) {
        showError(false);
        return;
      }

      var token = readAccessToken();
      if (!token) {
        showGate();
        return;
      }

      showOrder();
      list.setAttribute('aria-busy', 'true');
      var seq = ++requestSeq;

      fetchSelection(requested, token).then(function (result) {
        if (seq !== requestSeq) return;

        if (isValid(result.data)) {
          lines = result.data.items;
          renderLines(result.data);
          setCheckout(result.data.checkoutUrl);
          markActive();
          return;
        }

        if (isAccessRequired(result)) {
          clearAccessToken();
          showGate();
          return;
        }

        /* 422 = the selection itself is not purchasable; only offer the
           modal's checkout URL when the app could not be reached. */
        showError(result.status === 0 || result.status >= 500);
      });
    }

    function sendQuantities() {
      var items = pending;
      pending = null;
      var token = readAccessToken();
      if (!token) {
        requested = items;
        showGate();
        return;
      }
      var seq = ++requestSeq;

      fetchSelection(items, token).then(function (result) {
        if (seq !== requestSeq) return;

        if (isAccessRequired(result)) {
          clearAccessToken();
          requested = items;
          showGate();
          return;
        }

        if (isValid(result.data)) {
          lines = result.data.items;
          requested = items;
          renderLines(result.data);
          setCheckout(result.data.checkoutUrl);
          updateError.hidden = true;

          try {
            params.set('items', itemsString(items));
            window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
          } catch (error) {
            /* Enhancement only */
          }
          return;
        }

        /* Keep the last validated order and its checkout link. */
        renderLines({ items: lines, subtotal: lastSubtotal });
        setCheckout(lastCheckoutUrl);
        updateError.hidden = false;
      });
    }

    /* ---------- Events ---------- */

    list.addEventListener('click', function (event) {
      var button = event.target.closest('[data-step]');
      if (!button || !lines) return;

      var row = button.closest('[data-variant-id]');
      var current = pending || lines.map(function (l) {
        return { variantId: numericId(l.variantId), quantity: l.quantity };
      });

      var next = current.map(function (item) {
        if (item.variantId !== row.dataset.variantId) return item;
        var quantity = Math.min(MAX_QTY, Math.max(1, item.quantity + Number(button.dataset.step)));
        return { variantId: item.variantId, quantity: quantity };
      });

      var changed = next.some(function (item, i) { return item.quantity !== current[i].quantity; });
      if (!changed) return;

      pending = next;

      /* Show the new quantity right away; prices wait for the server. */
      var line = next.filter(function (i) { return i.variantId === row.dataset.variantId; })[0];
      row.querySelector('.ic-purchase__qty-value').textContent = String(line.quantity);
      row.querySelector('[data-step="-1"]').disabled = line.quantity <= 1;
      row.querySelector('[data-step="1"]').disabled = line.quantity >= MAX_QTY;
      row.querySelector('.ic-purchase__line-total').textContent = '…';
      subtotalEl.textContent = '…';
      var summaryRow = summaryLines && summaryLines.querySelector('[data-variant-id="' + row.dataset.variantId + '"]');
      if (summaryRow) {
        summaryRow.querySelector('.ic-purchase__summary-qty').textContent = ' × ' + line.quantity;
        summaryRow.querySelector('.ic-purchase__summary-amount').textContent = '…';
      }
      setBusy();

      window.clearTimeout(updateTimer);
      updateTimer = window.setTimeout(sendQuantities, UPDATE_DELAY_MS);
    });

    /* Funnel analytics: report the checkout click to the app (fire-and-forget).
       The access token goes in the POST body only; the server finds the request
       bound to it. Never delays or blocks the navigation to Shopify checkout. */
    function trackCheckoutClick() {
      var token = readAccessToken();
      if (!token) return;
      var url = endpoint.replace(/\/selection$/, '/event');
      var body = JSON.stringify({ token: token, type: 'checkout_clicked' });
      try {
        if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
      } catch (error) {
        /* fall through */
      }
      try {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: body, keepalive: true }).catch(function () {});
      } catch (error) {
        /* Analytics only */
      }
    }

    cta.addEventListener('click', function (event) {
      if (cta.getAttribute('aria-disabled') === 'true' || !cta.href) {
        event.preventDefault();
        return;
      }
      trackCheckoutClick();
    });

    retry.addEventListener('click', load);

    load();
  }

  function initAll() {
    document.querySelectorAll('[data-ic-purchase]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-ic-purchase]').forEach(init);
  });
})();
